(() => {
  const ROOT_SELECTOR = '[data-vur-root]';

  function trackEvent(event, detail = {}) {
    const payload = { event, ...detail };
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    window.dispatchEvent(new CustomEvent('vendeunareceta:event', { detail: payload }));

    // Puente hacia los Customer Events de Shopify: un Custom Pixel puede
    // suscribirse a estos eventos y reenviarlos al pixel de Meta (ViewContent,
    // AddToCart) sin que este archivo toque fbq directamente.
    try {
      window.Shopify?.analytics?.publish?.(`vur_${event}`, payload);
    } catch {
      // La landing no depende de la analítica para funcionar.
    }
  }

  function formatClp(value) {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0,
    }).format(value);
  }

  function initCalculator(root) {
    const calculator = root.querySelector('[data-vur-calculator]');
    if (!calculator) return;

    const inputs = Array.from(calculator.querySelectorAll('[data-calculator-field]'));
    const result = calculator.querySelector('[data-calculator-result]');
    const displayNodes = Array.from(calculator.querySelectorAll('[data-calculator-display]'));
    let analyticsTimer;

    const getValue = (field) => {
      const input = inputs.find((item) => item.dataset.calculatorField === field);
      return Number(input?.value || 0);
    };

    // El resultado salta de un número a otro; animarlo hace que se sienta
    // calculado en vez de precargado. Arranca desde el valor que se está
    // mostrando, así arrastrar el slider se ve continuo.
    const sinMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let tweenId = null;
    let mostrado = null;

    const animarResultado = (destino) => {
      if (!result) return;
      if (sinMovimiento || mostrado === null) {
        mostrado = destino;
        result.textContent = formatClp(destino);
        return;
      }
      const desde = mostrado;
      const inicio = performance.now();
      const duracion = 320;
      if (tweenId) cancelAnimationFrame(tweenId);

      const paso = (ahora) => {
        const t = Math.min((ahora - inicio) / duracion, 1);
        const suave = 1 - Math.pow(1 - t, 3);
        mostrado = Math.round(desde + (destino - desde) * suave);
        result.textContent = formatClp(mostrado);
        if (t < 1) tweenId = requestAnimationFrame(paso);
        else { mostrado = destino; result.textContent = formatClp(destino); }
      };
      tweenId = requestAnimationFrame(paso);
    };

    const render = () => {
      displayNodes.forEach((node) => {
        const field = node.dataset.calculatorDisplay;
        const value = getValue(field);
        node.textContent = field === 'precio' ? `$${value.toLocaleString('es-CL')}` : String(value);
      });

      animarResultado(getValue('precio') * getValue('unidades') * getValue('dias'));
    };

    // El campo numérico acepta cualquier cifra mientras se escribe, pero el
    // rango la recorta a su min/max. Sin esto, escribir 99999 dejaba el número
    // mostrando 99999 y el resultado calculado con 15000.
    const clamp = (input) => {
      if (input.value.trim() === '') return null;
      const min = Number(input.min);
      const max = Number(input.max);
      const value = Number(input.value);
      if (!Number.isFinite(value)) return null;
      return Math.min(Math.max(value, min), max);
    };

    const syncPeers = (input, value) => {
      const field = input.dataset.calculatorField;
      inputs
        .filter((item) => item !== input && item.dataset.calculatorField === field)
        .forEach((peer) => {
          peer.value = String(value);
        });
    };

    inputs.forEach((input) => {
      input.addEventListener('input', () => {
        const clamped = clamp(input);
        if (clamped === null) return;

        syncPeers(input, clamped);
        render();

        window.clearTimeout(analyticsTimer);
        analyticsTimer = window.setTimeout(() => {
          trackEvent('calculator_interaction', {
            field: input.dataset.calculatorField,
            value: clamped,
          });
        }, 250);
      });

      // Al salir del campo, el número escrito se normaliza al valor realmente
      // usado en el cálculo, para que nunca se muestren dos cifras distintas.
      input.addEventListener('change', () => {
        const clamped = clamp(input);
        const value = clamped === null ? Number(input.min) : clamped;
        input.value = String(value);
        syncPeers(input, value);
        render();
      });
    });

    render();
  }

  function initGallery(root) {
    const dialog = root.querySelector('[data-vur-lightbox]');
    if (!dialog) return;

    const image = dialog.querySelector('[data-vur-lightbox-image]');
    const title = dialog.querySelector('[data-vur-lightbox-title]');
    const closeButton = dialog.querySelector('[data-vur-lightbox-close]');
    const prevButton = dialog.querySelector('[data-vur-lightbox-prev]');
    const nextButton = dialog.querySelector('[data-vur-lightbox-next]');
    const counter = dialog.querySelector('[data-vur-lightbox-counter]');
    const triggers = Array.from(root.querySelectorAll('[data-vur-preview]'));
    if (!triggers.length) return;

    let index = 0;
    let opener = null;

    // Precargamos la lámina vecina para que pasar de página sea instantáneo,
    // sin descargar las siete de entrada.
    const preload = (i) => {
      const src = triggers[i]?.dataset.src;
      if (src) new Image().src = src;
    };

    const show = (i) => {
      index = (i + triggers.length) % triggers.length;
      const trigger = triggers[index];
      image.src = trigger.dataset.src || '';
      image.alt = trigger.dataset.alt || '';
      title.textContent = trigger.dataset.label || 'Vista ampliada';
      if (counter) counter.textContent = `${index + 1} / ${triggers.length}`;
      preload(index + 1 >= triggers.length ? 0 : index + 1);
      preload(index - 1 < 0 ? triggers.length - 1 : index - 1);
    };

    const close = () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      opener?.focus();
    };

    triggers.forEach((button, i) => {
      button.addEventListener('click', () => {
        opener = button;
        show(i);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        closeButton?.focus();
        trackEvent('preview_open', { label: button.dataset.label || '', index: i + 1 });
      });
    });

    prevButton?.addEventListener('click', () => show(index - 1));
    nextButton?.addEventListener('click', () => show(index + 1));

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
    });

    // Deslizar entre láminas en móvil.
    let startX = null;
    dialog.addEventListener('touchstart', (event) => {
      startX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true });
    dialog.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const delta = (event.changedTouches[0]?.clientX ?? startX) - startX;
      startX = null;
      if (Math.abs(delta) > 45) show(delta < 0 ? index + 1 : index - 1);
    }, { passive: true });

    closeButton?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
  }

  // Marca en qué extremo está cada carrusel (para apagar el degradado del borde
  // cuando ya no hay más contenido) y dibuja su barra de avance.
  function initScrollers(root) {
    root.querySelectorAll('.vur-scroller').forEach((scroller) => {
      const bar = document.createElement('div');
      bar.className = 'scroller-progress';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<span></span>';
      scroller.after(bar);
      const fill = bar.firstElementChild;

      const update = () => {
        const max = scroller.scrollWidth - scroller.clientWidth;
        const desplazable = max > 4;
        bar.hidden = !desplazable;
        scroller.classList.toggle('is-scroll-start', scroller.scrollLeft <= 2);
        scroller.classList.toggle('is-scroll-end', !desplazable || scroller.scrollLeft >= max - 2);
        if (desplazable) {
          const visible = scroller.clientWidth / scroller.scrollWidth;
          fill.style.width = `${Math.max(visible * 100, 12)}%`;
          fill.style.transform = `translateX(${(scroller.scrollLeft / max) * (100 / Math.max(visible, 0.12) - 100)}%)`;
        }
      };

      update();
      scroller.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    });
  }

  // Cuenta regresiva de la ventana de precio.
  //
  // Nunca se reinicia: al llegar a cero la banda se oculta y no vuelve. La fecha
  // llega ya resuelta desde Liquid con el huso de la tienda, y el servidor deja
  // de renderizar el bloque una vez pasada, así que el JS solo refresca el reloj.
  function initCountdown(root) {
    root.querySelectorAll('[data-vur-countdown]').forEach((banda) => {
      const porVisita = Number(banda.dataset.visitSeconds);
      let limite;

      if (Number.isFinite(porVisita) && porVisita > 0) {
        // Modo "desde que entra la persona". El instante de término se guarda en
        // sessionStorage para que recargar la página NO regale otros 15 minutos:
        // dentro de la misma sesión el reloj sigue donde iba.
        const clave = `vendeunareceta:ventana:${porVisita}`;
        let guardado = null;
        try {
          guardado = Number(sessionStorage.getItem(clave));
        } catch {
          // Sin almacenamiento disponible, arranca de cero en cada carga.
        }
        if (Number.isFinite(guardado) && guardado > Date.now()) {
          limite = guardado;
        } else if (guardado && guardado <= Date.now()) {
          banda.hidden = true;
          return;
        } else {
          limite = Date.now() + porVisita * 1000;
          try {
            sessionStorage.setItem(clave, String(limite));
          } catch {
            // La banda funciona igual durante esta carga.
          }
        }
      } else {
        // Modo "fecha fija". Liquid entrega los segundos que faltan, ya
        // calculados con el huso de la tienda, así el reloj no depende de la
        // zona horaria del visitante.
        const restanSegundos = Number(banda.dataset.remaining);
        if (!Number.isFinite(restanSegundos) || restanSegundos <= 0) {
          banda.hidden = true;
          return;
        }
        limite = Date.now() + restanSegundos * 1000;
      }

      // Con menos de un día por delante, mostrar "00 días" solo hace ruido.
      if (limite - Date.now() < 86400000) {
        banda.querySelector('[data-unit="d"]')?.remove();
      }

      const unidades = {};
      banda.querySelectorAll('[data-unit]').forEach((n) => {
        unidades[n.dataset.unit] = n.querySelector('b');
      });

      let timer;
      const tick = () => {
        const restante = limite - Date.now();
        if (restante <= 0) {
          banda.hidden = true;
          window.clearInterval(timer);
          return;
        }
        const s = Math.floor(restante / 1000);
        const val = {
          d: Math.floor(s / 86400),
          h: Math.floor((s % 86400) / 3600),
          m: Math.floor((s % 3600) / 60),
          s: s % 60,
        };
        Object.keys(unidades).forEach((k) => {
          if (unidades[k]) unidades[k].textContent = String(val[k]).padStart(2, '0');
        });
      };

      tick();
      timer = window.setInterval(tick, 1000);
    });
  }

  // Marca en el menú la sección que se está viendo.
  function initScrollSpy(root) {
    const links = Array.from(root.querySelectorAll('.desktop-nav a[href^="#"]'));
    if (!links.length || !('IntersectionObserver' in window)) return;

    const porId = new Map();
    links.forEach((link) => {
      const target = root.querySelector(link.getAttribute('href'));
      if (target) porId.set(target, link);
    });
    if (!porId.size) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const link = porId.get(entry.target);
          if (!link) return;
          if (entry.isIntersecting) {
            links.forEach((otro) => otro.classList.remove('is-current'));
            link.classList.add('is-current');
          }
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    porId.forEach((_, target) => observer.observe(target));
  }

  function initFaq(root) {
    root.querySelectorAll('[data-vur-faq]').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (details.open) {
          trackEvent('faq_open', {
            question: details.querySelector('summary')?.textContent?.trim() || '',
          });
        }
      });
    });
  }

  function initCheckoutEvents(root) {
    root.querySelectorAll('[data-vur-checkout]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const eventName = link.dataset.event || 'checkout_click';
        const checkoutUrl = link.getAttribute('href') || '';

        if (!/^https:\/\//i.test(checkoutUrl)) {
          event.preventDefault();
          return;
        }

        trackEvent(eventName, { placement: eventName.replace('_cta_click', '') });
        trackEvent('checkout_click', { source: eventName });
      });
    });
  }

  function initReveal(root) {
    const elements = Array.from(root.querySelectorAll('[data-reveal]'));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    // Numeramos cada elemento dentro de su contenedor para que las tarjetas de
    // una misma grilla entren escalonadas en vez de todas a la vez. El tope de 5
    // evita que la última de una grilla larga tarde casi medio segundo.
    const porContenedor = new Map();
    elements.forEach((element) => {
      const padre = element.parentElement;
      const i = porContenedor.get(padre) ?? 0;
      porContenedor.set(padre, i + 1);
      element.style.setProperty('--reveal-i', String(Math.min(i, 5)));
    });

    document.documentElement.classList.add('vur-reveal-enabled');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    elements.forEach((element) => observer.observe(element));
  }

  function initHeader(root) {
    const header = root.querySelector('[data-vur-header]');
    if (!header) return;

    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 16);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  function initSticky(root) {
    const bar = root.querySelector('[data-vur-sticky]');
    if (!bar) return;

    const closeButton = bar.querySelector('[data-vur-sticky-close]');
    const targets = Array.from(root.querySelectorAll('#oferta, .final-cta, .site-footer'));
    let purchaseSectionVisible = false;

    try {
      if (sessionStorage.getItem('vendeunareceta:sticky-closed') === '1') {
        bar.hidden = true;
        return;
      }
    } catch {
      // The bar remains available when storage is unavailable.
    }

    const update = () => {
      bar.classList.toggle('is-active', window.scrollY > 520 && !purchaseSectionVisible);
    };

    closeButton?.addEventListener('click', () => {
      bar.hidden = true;
      try {
        sessionStorage.setItem('vendeunareceta:sticky-closed', '1');
      } catch {
        // Closing still works for the current page.
      }
    });

    if ('IntersectionObserver' in window && targets.length) {
      const visibleTargets = new Set();
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) visibleTargets.add(entry.target);
            else visibleTargets.delete(entry.target);
          });
          purchaseSectionVisible = visibleTargets.size > 0;
          update();
        },
        { threshold: 0.08 }
      );
      targets.forEach((target) => observer.observe(target));
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  function init(root) {
    if (!root || root.dataset.vurInitialized === 'true') return;
    root.dataset.vurInitialized = 'true';
    initHeader(root);
    initCalculator(root);
    initGallery(root);
    initScrollers(root);
    initScrollSpy(root);
    initCountdown(root);
    initFaq(root);
    initCheckoutEvents(root);
    initReveal(root);
    initSticky(root);
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
